// @ts-strict-ignore
import type { FieldValues } from 'react-hook-form';
import {
  COUNTRIES,
  STATES_AND_MILITARY,
  STATES,
  US,
} from '~/dashboard/views/sars/form-view/constants/location.constant';
import { NAICS_CODES } from '~/dashboard/views/sars/form-view/constants/naics.constant';
import type {
  Form, FormConfiguration, SarEntity, SarEntityPhoneNumber,
} from '../types';

// importing this from the redux helpers causes the reducer to fail in SarTrxSubForm.test.tsx, so we're redefining it here
export const nullOrFormatToInteger = amount => ((amount || amount === 0)
  ? Math.trunc(amount.replace(/[$,]/g, '') * 100)
  : null);

export const INDEX_REFERENCE = 'INDEX_REFERENCE';
export const PAGE_INDEX = 'PAGE_INDEX';
export const PARENT_INDEX = 'PARENT_INDEX';

// page name definitions, so that we can worry less about typos and copy changes
export const OVERVIEW = 'overview';
export const SAR_FILING_INSTITUTION = 'filingInstitution';
export const SAR_ACTIVITY_INSTITUTION = 'sarActivityInstitution';
export const ENTITY = 'entities';
export const ACTIVITY_INFORMATION = 'activityInformation';
export const NARRATIVE = 'narrative';
export const NEXT_STEPS = 'nextSteps';

export const FormStyle = {
  width: '50%',
};

export const CheckboxStyle = {
  width: '100%',
};

export interface FormInputProps {
  form: FormConfiguration;
  register: Register;
  errors?: any;
  watch?: any;
  isDraft?: boolean;
  formMap?: FieldValues;
}

export interface Register {
  (name: string, RegisterOptions?): ({ onChange; onBlur; name; ref });
}

export const ERROR_MESSAGES = {
  amountAboveZero: 'Amount should be greater than zero',
  currencyFormat: 'Please provide a number or a recognized currency format',
  dateFormat: 'Please use MM/DD/YYYY format',
  foreignTin: 'Please provide 25 characters or less',
  noteToFincen: 'Please provide 50 characters or less',
  required: 'This is a required field',
  tin: 'Please provide a 9-digit TIN value',
  alphanumeric: 'Do not include punctuation or formatting such as hyphens, periods, or spaces',
  nonprintableCharacters: 'Do not include non-printable characters such as carriage returns, line feeds, and tabs',
};

export const VALIDATION_PATTERNS = {
  dateFormat: /^(0[0-9]|1[0-2])\/(0[0-9]|1[0-9]|2[0-9]|3[01])\/(19|20)[0-9][0-9]$/g,
  alphanumeric: /^[a-zA-Z0-9]+$/g,
  nonprintableCharacters: /[^ -~]/g,
};

// produce a standard regex match validation function
const createRegExValidationFn = errorKey => (
  value => ({ [errorKey]: !!value && !value?.match(VALIDATION_PATTERNS[errorKey]) })
);

const QUESTION_24_CHECKBOX_OPTIONS = [
  { label: 'B. Accountant', value: 'accountant' },
  { label: 'C. Agent', value: 'agent' },
  { label: 'D. Appraiser', value: 'appraiser' },
  { label: 'E. Attorney', value: 'attorney' },
  { label: 'F. Borrower', value: 'borrower' },
  { label: 'G. Customer', value: 'customer' },
  { label: 'H. Director', value: 'director' },
  { label: 'I. Employee', value: 'employee' },
  { label: 'J. No relationship to institution', value: 'no relationship to institution' },
  { label: 'K. Officer', value: 'officer' },
  { label: 'L. Owner or Controlling Shareholder', value: 'owner or controlling shareholder' },
  { label: 'z. Other', value: 'other' },
];

const QUESTION_32_CHECKBOX_OPTIONS = [
  { label: 'a. Alters or cancels transaction to avoid BSA recordkeeping requirement', value: '111' },
  { label: 'b. Alters or cancels transaction to avoid CTR requirement', value: '112' },
  { label: 'c. Suspicious inquiry by customer regarding BSA reporting or recordkeeping requirements', value: '106' },
  { label: 'd. Transaction(s) below BSA recordkeeping threshold', value: '113' },
  { label: 'e. Transaction(s) below CTR threshold', value: '114' },
  { label: 'z. Other', value: '1999' },
];

const QUESTION_33_CHECKBOX_OPTIONS = [
  { label: 'a. Known or suspected terrorist/terrorist organization', value: '701' },
  { label: 'z. Other', value: '7999' },
];

const QUESTION_34_CHECKBOX_OPTIONS = [
  { label: 'a. ACH', value: '320' },
  { label: 'b. Advance fee', value: '322' },
  { label: 'c. Business loan', value: '321' },
  { label: 'd. Check', value: '301' },
  { label: 'e. Consumer loan', value: '304' },
  { label: 'f. Credit/Debit card', value: '305' },
  { label: 'g. Healthcare/Public or private health insurance', value: '323' },
  { label: 'h. Mail', value: '308' },
  { label: 'i. Mass-marketing', value: '309' },
  { label: 'j. Ponzi scheme', value: '324' },
  { label: 'k. Pyramid scheme', value: '310' },
  { label: 'l. Securities fraud', value: '325' },
  { label: 'm. Wire', value: '312' },
  { label: 'z. Other', value: '3999' },
];

const QUESTION_35_CHECKBOX_OPTIONS = [
  { label: 'a. Chip walking', value: '1201' },
  { label: 'b. Minimal gaming with large transactions', value: '1202' },
  { label: 'c. Suspicious use of counter checks or markers', value: '1203' },
  { label: 'd. Unknown source of chips', value: '1204' },
  { label: 'z. Other', value: '12999' },
];

const QUESTION_36_CHECKBOX_OPTIONS = [
  { label: 'a. Exchange small bills for large bills or vice versa', value: '801' },
  { label: 'b. Funnel account', value: '824' },
  { label: 'c. Suspicion concerning the physical condition of funds', value: '820' },
  { label: 'd. Suspicion concerning the source of funds', value: '821' },
  { label: 'e. Suspicious designation of beneficiaries, assignees or joint owners', value: '804' },
  { label: 'f. Suspicious EFT/wire transfers', value: '805' },
  { label: 'g. Suspicious exchange of currencies', value: '822' },
  { label: 'h. Suspicious receipt of government payments/benefits', value: '806' },
  { label: 'i. Suspicious use of multiple accounts', value: '807' },
  { label: 'j. Suspicious use of non-cash monetary instruments', value: '808' },
  { label: 'k. Suspicious use of third-party transactors (straw-man)', value: '809' },
  { label: 'l. Trade Based Money Laundering/Black Market Peso Exchange', value: '823' },
  { label: 'm. Transaction out of pattern for customer(s)', value: '812' },
  { label: 'z. Other', value: '8999' },
];

const QUESTION_37_CHECKBOX_OPTIONS = [
  { label: 'a. Changes spelling or arrangement of name', value: '401' },
  { label: 'b. Multiple individuals with same or similar identities', value: '402' },
  { label: 'c. Provided questionable or false documentation', value: '403' },
  { label: 'd. Provided questionable or false identification', value: '409' },
  { label: 'e. Refused or avoided request for documentation', value: '404' },
  { label: 'f. Single individual with multiple identities', value: '405' },
  { label: 'z. Other', value: '4999' },
];

const QUESTION_38_CHECKBOX_OPTIONS = [
  { label: 'a. Account takeover', value: '920' },
  { label: 'b. Bribery or gratuity', value: '901' },
  { label: 'c. Counterfeit Instruments', value: '917' },
  { label: 'd. Elder financial exploitation', value: '921' },
  { label: 'e. Embezzlement/theft/disappearance of funds', value: '903' },
  { label: 'f. Forgeries', value: '904' },
  { label: 'g. Human smuggling', value: '926' },
  { label: 'h. Human trafficking', value: '927' },
  { label: 'i. Identity theft', value: '905' },
  { label: 'j. Little or no concern for product performance, penalities, fees, or tax consequences', value: '922' },
  { label: 'k. Misuse of position or self-dealing', value: '924' },
  { label: 'l. Suspected public/private corruption (domestic)', value: '907' },
  { label: 'm. Suspected public/private corruption (foreign)', value: '908' },
  { label: 'n. Suspicious use of informal value transfer system', value: '909' },
  { label: 'o. Suspicious use of multiple transaction locations', value: '910' },
  { label: 'p. Transaction with no apparent economic, business, or lawful purpose', value: '925' },
  { label: 'q. Transaction(s) involving foreign high risk jurisdiction', value: '928' },
  { label: 'q. Two or more individuals working together', value: '911' },
  { label: 's. Unlicensed or unregistered MSB', value: '913' },
  { label: 'z. Other', value: '9999' },
];

const QUESTION_39_CHECKBOX_OPTIONS = [
  { label: 'a. Excessive insurance', value: '501' },
  { label: 'b. Excessive or unusual cash borrowing against policy/annuity', value: '502' },
  { label: 'c. Proceeds sent to or received from unrelated third party', value: '504' },
  { label: 'd. Suspicious life settlement sales insurance (e.g., STOLI\'s, Viaticals)', value: '505' },
  { label: 'e. Suspicious termination of policy or contract', value: '506' },
  { label: 'f. Unclear or no insurable interest', value: '507' },
  { label: 'z. Other', value: '5999' },
];

const QUESTION_40_CHECKBOX_OPTIONS = [
  { label: 'a. Insider trading', value: '601' },
  { label: 'b. Market manipulation', value: '608' },
  { label: 'c. Misappropriation', value: '603' },
  { label: 'd. Unauthorized pooling', value: '604' },
  { label: 'e. Wash trading', value: '609' },
  { label: 'z. Other', value: '6999' },
];

const QUESTION_41_CHECKBOX_OPTIONS = [
  { label: 'a. Application fraud', value: '1005' },
  { label: 'b. Appraisal fraud', value: '1001' },
  { label: 'c. Foreclosure/Short sale fraud', value: '1006' },
  { label: 'd. Loan Modification fraud', value: '1003' },
  { label: 'e. Origination fraud', value: '1007' },
  { label: 'z. Other', value: '10999' },
];

const QUESTION_42_CHECKBOX_OPTIONS = [
  { label: 'a. Against Financial Institution(s)', value: '1101' },
  { label: 'b. Against Financial Institution Customer(s)', value: '1102' },
  { label: 'z. Other', value: '11999' },
];

const QUESTION_45_CHECKBOX_OPTIONS = [
  { label: 'a. Bonds/Notes', value: '2' },
  { label: 'b. Commercial mortgage', value: '3' },
  { label: 'c. Commercial paper', value: '4' },
  { label: 'd. Credit card', value: '5' },
  { label: 'e. Debit card', value: '6' },
  { label: 'f. Deposit account', value: '46' },
  { label: 'g. Forex transactions', value: '7' },
  { label: 'h. Futures/Options on futures', value: '8' },
  { label: 'k. Hedge fund', value: '9' },
  { label: 'l. Home equity line of credit', value: '11' },
  { label: 'm. Home equity loan', value: '10' },
  { label: 'n. Insurance/Annuity products', value: '12' },
  { label: 'o. Options on securities', value: '14' },
  { label: 'p. Prepaid access', value: '16' },
  { label: 'q. Residential mortgage', value: '17' },
  { label: 'r. Security futures products', value: '18' },
  { label: 's. Stocks', value: '19' },
  { label: 't. Swap, hybrid, or other derivatives', value: '20' },
  { label: 'u. Micro-cap securities', value: '47' },
  { label: 'v. Mutual fund', value: '13' },
  { label: 'z. Other', value: '30' },
];

const QUESTION_46_CHECKBOX_OPTIONS = [
  { label: 'a. Bank/Cashier\'s check', value: '31' },
  { label: 'b. Foreign currency', value: '32' },
  { label: 'c. Funds transfer', value: '33' },
  { label: 'd. Gaming instruments', value: '34' },
  { label: 'e. Government payment', value: '35' },
  { label: 'f. Money orders', value: '36' },
  { label: 'g. Personal/Business check', value: '37' },
  { label: 'h. Travelers check', value: '38' },
  { label: 'i. U.S. Currency', value: '39' },
  { label: 'z. Other', value: '41' },
];

const PRIMARY_REGULATOR_TYPE_OPTIONS = [
  {
    label: 'Commodities Futures Trading Commission (CFTC)',
    value: 'CFTC',
  },
  {
    label: 'Federal Reserve Board (FBR)',
    value: 'FBR',
  },
  {
    label: 'Federal Deposit Insurance Corporation (FDIC)',
    value: 'FDIC',
  },
  {
    label: 'Federal Housing Finance Agency (FHFA)',
    value: 'FHFA',
  },
  {
    label: 'Internal Revenue Service (IRS)',
    value: 'IRS',
  },
  {
    label: 'National Credit Union Administration (NCUA)',
    value: 'NCUA',
  },
  {
    label: 'Office of the Comptroller of the Currency (OCC)',
    value: 'OCC',
  },
  {
    label: 'Securities and Exchange Commission (SEC)',
    value: 'SEC',
  },
  {
    label: 'Not Applicable',
    value: 'N_A',
  },
];

export const FRAUD_ID_SECTION_MAP = {
  ...QUESTION_32_CHECKBOX_OPTIONS.reduce((acc, { value }) => ({ ...acc, [value]: 'structuring' }), {}),
  ...QUESTION_33_CHECKBOX_OPTIONS.reduce((acc, { value }) => ({ ...acc, [value]: 'terr_fin' }), {}),
  ...QUESTION_34_CHECKBOX_OPTIONS.reduce((acc, { value }) => ({ ...acc, [value]: 'fraud' }), {}),
  ...QUESTION_35_CHECKBOX_OPTIONS.reduce((acc, { value }) => ({ ...acc, [value]: 'gaming' }), {}),
  ...QUESTION_36_CHECKBOX_OPTIONS.reduce((acc, { value }) => ({ ...acc, [value]: 'laundering' }), {}),
  ...QUESTION_37_CHECKBOX_OPTIONS.reduce((acc, { value }) => ({ ...acc, [value]: 'documentation' }), {}),
  ...QUESTION_38_CHECKBOX_OPTIONS.reduce((acc, { value }) => ({ ...acc, [value]: 'misc' }), {}),
  ...QUESTION_39_CHECKBOX_OPTIONS.reduce((acc, { value }) => ({ ...acc, [value]: 'insurance' }), {}),
  ...QUESTION_40_CHECKBOX_OPTIONS.reduce((acc, { value }) => ({ ...acc, [value]: 'securities' }), {}),
  ...QUESTION_41_CHECKBOX_OPTIONS.reduce((acc, { value }) => ({ ...acc, [value]: 'mortgage' }), {}),
  ...QUESTION_42_CHECKBOX_OPTIONS.reduce((acc, { value }) => ({ ...acc, [value]: 'cyber' }), {}),
  ...QUESTION_45_CHECKBOX_OPTIONS.reduce((acc, { value }) => ({ ...acc, [value]: 'product' }), {}),
  ...QUESTION_46_CHECKBOX_OPTIONS.reduce((acc, { value }) => ({ ...acc, [value]: 'instrument' }), {}),
};

const fraudIdQuestionMapReducer = (acc, { label, value }) => ({ ...acc, [value]: label.slice(3) });

export const FRAUD_ID_QUESTION_MAP = {
  ...QUESTION_32_CHECKBOX_OPTIONS.reduce(fraudIdQuestionMapReducer, {}),
  ...QUESTION_33_CHECKBOX_OPTIONS.reduce(fraudIdQuestionMapReducer, {}),
  ...QUESTION_34_CHECKBOX_OPTIONS.reduce(fraudIdQuestionMapReducer, {}),
  ...QUESTION_35_CHECKBOX_OPTIONS.reduce(fraudIdQuestionMapReducer, {}),
  ...QUESTION_36_CHECKBOX_OPTIONS.reduce(fraudIdQuestionMapReducer, {}),
  ...QUESTION_37_CHECKBOX_OPTIONS.reduce(fraudIdQuestionMapReducer, {}),
  ...QUESTION_38_CHECKBOX_OPTIONS.reduce(fraudIdQuestionMapReducer, {}),
  ...QUESTION_39_CHECKBOX_OPTIONS.reduce(fraudIdQuestionMapReducer, {}),
  ...QUESTION_40_CHECKBOX_OPTIONS.reduce(fraudIdQuestionMapReducer, {}),
  ...QUESTION_41_CHECKBOX_OPTIONS.reduce(fraudIdQuestionMapReducer, {}),
  ...QUESTION_42_CHECKBOX_OPTIONS.reduce(fraudIdQuestionMapReducer, {}),
  ...QUESTION_45_CHECKBOX_OPTIONS.reduce(fraudIdQuestionMapReducer, {}),
  ...QUESTION_46_CHECKBOX_OPTIONS.reduce(fraudIdQuestionMapReducer, {}),
};

const QUESTION_68_CHECKBOX_OPTIONS = [
  { label: 'Selling location', value: 'selling_location' },
  { label: 'Paying location', value: 'paying_location' },
  { label: 'Both', value: 'both' },
];

const QUESTION_56_CHECKBOX_OPTIONS = [
  { label: 'Selling location', value: 'selling_location' },
  { label: 'Paying location', value: 'paying_location' },
  { label: 'Both', value: 'both' },
];

const QUESTION_53_CHECKBOX_OPTIONS = [
  { label: 'State licensed casino', value: 'state_licensed_casino' },
  { label: 'Tribal authorized casino', value: 'tribal_authorized_casino' },
  { label: 'Card club', value: 'card_club' },
  { label: 'Other', value: 'other' },
];

const QUESTION_54_CHECKBOX_OPTIONS = [
  { label: 'Clearing Broker-Securities', value: 'clearing_broker_securities' },
  { label: 'Introducing Broker-Securities', value: 'introducing_broker_securities' },
  { label: 'Subsidiary of Financial/Bank Holding Company', value: 'subsidary_of_financial_bank_holding_company' },
  { label: 'Execution-Only Broker Securities', value: 'execution-only_broker_securities' },
  { label: 'Investment Adviser', value: 'investment_adviser' },
  { label: 'Futures Commission Merchant', value: 'futures_commission_merchant' },
  { label: 'Investment Company', value: 'investment_company' },
  { label: 'Holding Company', value: 'holding_company' },
  { label: 'Retail Foreign Exchange Dealer', value: 'retail_foreign_exchange_dealer' },
  { label: 'Introducing Broker-Commodities', value: 'introducing_broker_commodities' },
  { label: 'Self-Clearing Broker Securities', value: 'self_clearing_broker_securities' },
  { label: 'Other', value: 'other' },
];

const QUESTION_80_CHECKBOX_OPTIONS = [
  { label: 'Clearing Broker-Securities', value: 'clearing_broker_securities' },
  { label: 'Introducing Broker-Securities', value: 'introducing_broker_securities' },
  { label: 'SRO Securities', value: 'sro_securities' },
  { label: 'SRO Futures', value: 'sro_futures' },
  { label: 'CPO/CTA', value: 'cpo_cta' },
  { label: 'Investment Adviser', value: 'investment_adviser' },
  { label: 'Subsidiary of Financial/Bank Holding Company', value: 'subsidary_of_financial_bank_holding_company' },
  { label: 'Execution-Only Broker Securities', value: 'execution_only_broker_securities' },
  { label: 'Investment Company', value: 'investment_company' },
  { label: 'Futures Commission Merchant', value: 'futures_commission_merchant' },
  { label: 'Retail Foreign Exchange Dealer', value: 'retail_foregin_exchange_dealer' },
  { label: 'Holding Company', value: 'holding_company' },
  { label: 'Self-Clearing Broker Securities', value: 'self_clearing_broker_securities' },
  { label: 'Introducing Broker-Commodities', value: 'introducing_broker_commodities' },
  { label: 'Other', value: 'other' },
];

const NOTIFICATION_DAYS_OPTIONS = new Array(91)
  .fill(null)
  .map((value, index) => ({
    label: `${index}`,
    value: `${index}`,
  }));

const initializeOptions = options => (
  options.reduce((acc, { value }) => {
    acc[value] = false;
    return acc;
  }, {})
);

export const QUESTION_24_CHECKBOX_INITIAL = initializeOptions(QUESTION_24_CHECKBOX_OPTIONS);
export const QUESTION_32_CHECKBOX_INITIAL = initializeOptions(QUESTION_32_CHECKBOX_OPTIONS);
export const QUESTION_33_CHECKBOX_INITIAL = initializeOptions(QUESTION_33_CHECKBOX_OPTIONS);
export const QUESTION_34_CHECKBOX_INITIAL = initializeOptions(QUESTION_34_CHECKBOX_OPTIONS);
export const QUESTION_35_CHECKBOX_INITIAL = initializeOptions(QUESTION_35_CHECKBOX_OPTIONS);
export const QUESTION_36_CHECKBOX_INITIAL = initializeOptions(QUESTION_36_CHECKBOX_OPTIONS);
export const QUESTION_37_CHECKBOX_INITIAL = initializeOptions(QUESTION_37_CHECKBOX_OPTIONS);
export const QUESTION_38_CHECKBOX_INITIAL = initializeOptions(QUESTION_38_CHECKBOX_OPTIONS);
export const QUESTION_39_CHECKBOX_INITIAL = initializeOptions(QUESTION_39_CHECKBOX_OPTIONS);
export const QUESTION_40_CHECKBOX_INITIAL = initializeOptions(QUESTION_40_CHECKBOX_OPTIONS);
export const QUESTION_41_CHECKBOX_INITIAL = initializeOptions(QUESTION_41_CHECKBOX_OPTIONS);
export const QUESTION_42_CHECKBOX_INITIAL = initializeOptions(QUESTION_42_CHECKBOX_OPTIONS);
export const QUESTION_45_CHECKBOX_INITIAL = initializeOptions(QUESTION_45_CHECKBOX_OPTIONS);
export const QUESTION_46_CHECKBOX_INITIAL = initializeOptions(QUESTION_46_CHECKBOX_OPTIONS);
export const QUESTION_53_CHECKBOX_INITIAL = initializeOptions(QUESTION_53_CHECKBOX_OPTIONS);
export const QUESTION_54_CHECKBOX_INITIAL = initializeOptions(QUESTION_54_CHECKBOX_OPTIONS);
export const QUESTION_56_CHECKBOX_INITIAL = initializeOptions(QUESTION_56_CHECKBOX_OPTIONS);
export const QUESTION_68_CHECKBOX_INITIAL = initializeOptions(QUESTION_68_CHECKBOX_OPTIONS);
export const QUESTION_80_CHECKBOX_INITIAL = initializeOptions(QUESTION_80_CHECKBOX_OPTIONS);

export const ENTITY_ADDRESS_INITIAL = {
  address: '',
  city: '',
  state: '',
  country: '',
  zip: '',
};

export const SAR_ENTITY_IDS_INITIAL = {
  country: '',
  id_num: '',
  id_type: '',
  state: '',
  other_value: '',
};

export const RELATIONSHIP_TINS_INITIAL = {
  action_date: '',
  institution_tin: '',
  position: { ...QUESTION_24_CHECKBOX_INITIAL },
  other_value: '',
  status: null,
};

export const FINANCIAL_TINS_INITIAL = {
  accounts: [{
    account_number: '',
    closed: false,
  }],
  institution_tin: '',
  non_us_financial_institution: false,
};

export const IP_ADDRESSES_INITIAL = {
  address: '',
  date: null,
  timestamp: '',
};

export const PHONE_NUMBERS_INITIAL: SarEntityPhoneNumber = {
  value: '',
  ext: '',
  type: '',
};

export const CYBER_FRAUDS_INITIAL = {
  sar_cyber_event_date: '',
  sar_cyber_event_type: '',
  sar_cyber_event_value: '',
  other_value: '',
};

export const BRANCH_INFORMATION_INITIAL = {
  address: '',
  branch_role: { ...QUESTION_68_CHECKBOX_INITIAL },
  city: '',
  country: '',
  rssd_number: '',
  state: '',
  zip: '',
};

export const SAR_ENTITY_INITIAL: SarEntity = {
  action_date: '',
  alternate_names_encrypted: [''],
  dob_encrypted: '',
  email_addresses_encrypted: [''],
  entity_addresses_encrypted: [{ ...ENTITY_ADDRESS_INITIAL }],
  entity_token: null,
  financial_tins: [{ ...FINANCIAL_TINS_INITIAL }],
  financial_tins_unknown: false,
  first_name_encrypted: '',
  gender_encrypted: '',
  has_statement: false,
  id_type: '',
  info_unavailable: false,
  is_entity: false,
  last_name_encrypted: '',
  middle_name_encrypted: '',
  naics_code: '',
  occupation: '',
  phone_numbers_encrypted: [{ ...PHONE_NUMBERS_INITIAL }],
  relationship_tins: [{ ...RELATIONSHIP_TINS_INITIAL }],
  role_in_activity: '',
  sar_entity_ids: [{ ...SAR_ENTITY_IDS_INITIAL }],
  sar_entity_ids_unknown: false,
  statement_in_activity: false,
  suffix_encrypted: '',
  tax_id_encrypted: '',
  tin: '',
  tin_type: '',
  websites: [''],
};


export const FORM_CONTROLS_V1: Form = {
  steps: {
    [ENTITY]: {
      header: 'Part 3: Subject Information',
      pageLabel: 'Subject Information',
      subheader: [
        'The following fields have been autofilled using the information we have available.',
        'Please carefully review and fill in all necessary information.',
      ],
      canAddPages: true,
      initialPageValue: () => SAR_ENTITY_INITIAL,
      multiPageLabel: 'Subject',
      path: ['entities'],
      controls: [
        {
          prop: 'question_3',
          type: 'multi',
          multiTypes: [
            {
              path: ['entities', PAGE_INDEX, 'is_entity'],
              prop: 'question_3_entity',
              type: 'checkbox-single',
              width: '40%',
              label: '3. Check',
              options: [{ label: 'If entity' }],
            },
            {
              path: ['entities', PAGE_INDEX, 'info_unavailable'],
              prop: 'question_3_pii_unavailable',
              type: 'checkbox-single',
              options: [{ label: 'If all critical subject information is unavailable' }],
            },
          ],
        },
        {
          label: '4. Individual\'s last name or entity\'s legal name',
          path: ['entities', PAGE_INDEX, 'last_name_encrypted'],
          prop: 'question_4_text',
          width: '20.375rem',
          type: 'textfield',
          canBeUnknown: true,
          unknownSectionController: ['entities', PAGE_INDEX, 'info_unavailable'],
        },
        {
          label: '5. First name',
          path: ['entities', PAGE_INDEX, 'first_name_encrypted'],
          prop: 'question_5_text',
          width: '20.375rem',
          type: 'textfield',
          canBeUnknown: true,
          disabled: {
            isFieldDisabled: val => val === true,
            dependency: ['entities', PAGE_INDEX, 'is_entity'],
          },
          validation: {
            dependency: ['entities', PAGE_INDEX, 'is_entity'],
            validator: (val, dep) => ({ required: !dep && !val }),
          },
          unknownSectionController: ['entities', PAGE_INDEX, 'info_unavailable'],
        },
        {
          question: 6,
          label: 'Middle name/initial',
          optional: 'Optional',
          prop: 'question_6',
          width: '15rem',
          type: 'textfield',
          path: ['entities', PAGE_INDEX, 'middle_name_encrypted'],
          disabled: {
            isFieldDisabled: val => val === true,
            dependency: ['entities', PAGE_INDEX, 'is_entity'],
          },
        },
        {
          label: '',
          prop: 'question_7',
          width: '100%',
          type: 'multi',
          multiTypes: [
            {
              path: ['entities', PAGE_INDEX, 'suffix_encrypted'],
              label: '7. Suffix',
              prop: 'question_7_text',
              width: '15rem',
              type: 'textfield',
              optional: 'Optional',
              disabled: {
                isFieldDisabled: val => val === true,
                dependency: ['entities', PAGE_INDEX, 'is_entity'],
              },
            },
            {
              path: ['entities', PAGE_INDEX, 'gender_encrypted'],
              label: '8. Gender',
              prop: 'question_8_dropdown',
              width: '15rem',
              type: 'select',
              disabled: {
                isFieldDisabled: val => val === true,
                dependency: ['entities', PAGE_INDEX, 'is_entity'],
              },
              placeholder: 'Select',
              optional: 'Optional',
              options: [
                {
                  label: 'Male',
                  value: 'male',
                },
                {
                  label: 'Female',
                  value: 'female',
                },
                {
                  label: 'Unknown',
                  value: 'unknown',
                },
              ],
            },
          ],
        },
        {
          question: 19,
          label: 'Date of birth',
          path: ['entities', PAGE_INDEX, 'dob_encrypted'],
          prop: 'question_19_text',
          width: '20.375rem',
          type: 'textfield',
          validation: {
            dependency: ['entities', PAGE_INDEX, 'is_entity'],
            validator: (val, dep) => {
              const result = {
                ...createRegExValidationFn('dateFormat')(val),
                required: !dep && !val,
              };
              return result;
            },
          },
          placeholder: 'MM/DD/YYYY',
          canBeUnknown: true,
          unknownSectionController: ['entities', PAGE_INDEX, 'info_unavailable'],
          disabled: {
            isFieldDisabled: val => val === true,
            dependency: ['entities', PAGE_INDEX, 'is_entity'],
          },
        },
        {
          question: 9,
          label: 'Alternate name, e.g., AKA - individual or trade name, DBA - business entity',
          path: ['entities', PAGE_INDEX, 'alternate_names_encrypted'],
          prop: 'question_9_additive',
          type: 'additive',
          optional: 'Optional',
          initialValue: '',
          button: 'Add Another Name',
          group: [
            {
              path: ['entities', PAGE_INDEX, 'alternate_names_encrypted', INDEX_REFERENCE],
              prop: 'question_9',
              type: 'textfield',
            },
          ],
        },
        {
          question: 10,
          label: 'Occupation of type of business',
          path: ['entities', PAGE_INDEX, 'occupation'],
          prop: 'question_10',
          width: '15rem',
          type: 'textfield',
          optional: 'Optional',
        },
        {
          question: '10a',
          label: 'NAICS Code',
          path: ['entities', PAGE_INDEX, 'naics_code'],
          prop: 'question_10a',
          width: '31rem',
          type: 'select',
          placeholder: 'Select',
          optional: 'Optional',
          options: NAICS_CODES.options,
        },
        {
          label: '',
          prop: 'question_16',
          type: 'multi',
          multiTypes: [
            {
              label: '16. TIN',
              path: ['entities', PAGE_INDEX, 'tax_id_encrypted'],
              prop: 'question_16_text',
              width: '20rem',
              type: 'textfield',
              validation: {
                dependency: ['entities', PAGE_INDEX, 'tin_type'],
                validator: (value, dependencyValue) => {
                  if (!value) {
                    return { tin: false, foreignTin: false };
                  }
                  if (dependencyValue === 'ein' || dependencyValue === 'ssn-itin') {
                    return value.match(/^([0-9][0-9][0-9][0-9][0-9][0-9][0-9][0-9][0-9])$/)
                      ? { tin: false, foreignTin: false }
                      : { tin: true, foreignTin: false };
                  }
                  return value.length <= 25
                    ? { tin: false, foreignTin: false }
                    : { tin: false, foreignTin: true };
                },
              },
              canBeUnknown: 'REQUIRED',
              unknownSectionController: ['entities', PAGE_INDEX, 'info_unavailable'],
            },
            {
              label: '17. TIN type',
              path: ['entities', PAGE_INDEX, 'tin_type'],
              prop: 'question_17',
              width: '15rem',
              type: 'select',
              placeholder: 'Select',
              options: [
                {
                  label: 'EIN',
                  value: 'ein',
                },
                {
                  label: 'SSN-ITIN',
                  value: 'ssn-itin',
                },
                {
                  label: 'Foreign',
                  value: 'foreign',
                },
              ],
            },
          ],
        },
        {
          label: 'Phone Number',
          question: 21,
          path: ['entities', PAGE_INDEX, 'phone_numbers_encrypted'],
          prop: 'question_21_additive',
          type: 'additive',
          optional: 'Optional',
          initialValue: { ...PHONE_NUMBERS_INITIAL },
          button: 'Add Another Phone Number',
          group: [
            {
              path: ['entities', PAGE_INDEX, 'phone_numbers_encrypted', INDEX_REFERENCE, 'value'],
              prop: 'question_21_value',
              type: 'textfield',
              initialValue: '',
              placeholder: 'Phone number',
            },
            {
              path: ['entities', PAGE_INDEX, 'phone_numbers_encrypted', INDEX_REFERENCE, 'ext'],
              prop: 'question_21_ext',
              type: 'textfield',
              initialValue: '',
              placeholder: 'Ext',
            },
            {
              path: ['entities', PAGE_INDEX, 'phone_numbers_encrypted', INDEX_REFERENCE, 'type'],
              prop: 'question_21_type',
              type: 'select',
              placeholder: 'Type',
              options: [
                {
                  label: 'Home',
                  value: 'home',
                },
                {
                  label: 'Work',
                  value: 'work',
                },
                {
                  label: 'Mobile',
                  value: 'mobile',
                },
                {
                  label: 'Fax',
                  value: 'fax',
                },
              ],
            },
          ],
        },
        {
          question: 22,
          label: 'Email Address',
          path: ['entities', PAGE_INDEX, 'email_addresses_encrypted'],
          prop: 'question_22_additive',
          type: 'additive',
          initialValue: '',
          button: 'Add Another Email Address',
          group: [{
            type: 'textfield',
            prop: 'question_22',
            path: ['entities', PAGE_INDEX, 'email_addresses_encrypted', INDEX_REFERENCE],
          }],
        },
        {
          question: '22a',
          label: 'Website (URL) address',
          path: ['entities', PAGE_INDEX, 'websites'],
          prop: 'question_22a_additive',
          type: 'additive',
          initialValue: '',
          button: 'Add Another Website',
          group: [{
            type: 'textfield',
            prop: 'question_22a',
            path: ['entities', PAGE_INDEX, 'websites', INDEX_REFERENCE],
          }],
        },
        {
          label: '',
          prop: 'question_23',
          width: '50%',
          type: 'multi',
          multiTypes: [
            {
              question: 23,
              label: 'Corroborative statement to filer?',
              path: ['entities', PAGE_INDEX, 'statement_in_activity'],
              prop: 'question_23_dropdown',
              width: '15rem',
              disabled: {
                isFieldDisabled: val => val === true,
                dependency: ['entities', PAGE_INDEX, 'is_entity'],
              },
              type: 'select',
              placeholder: 'Select',
              options: [
                {
                  label: 'Yes',
                  value: 'true',
                },
                {
                  label: 'No',
                  value: 'false',
                },
              ],
            },
            {
              question: 28,
              label: 'Subject\'s role in suspicious activity',
              path: ['entities', PAGE_INDEX, 'role_in_activity'],
              prop: 'question_28_dropdown',
              width: '15rem',
              type: 'select',
              placeholder: 'Select',
              options: [
                {
                  label: 'Purchaser/Sender',
                  value: 'purchaser_sender',
                },
                {
                  label: 'Payee/Receiver',
                  value: 'payee_receiver',
                },
                {
                  label: 'Both',
                  value: 'both',
                },
              ],
            },
          ],
        },
        {
          label: 'Subject Address Information',
          prop: '',
          type: 'section',
          group: [
            {
              path: ['entities', PAGE_INDEX, 'entity_addresses_encrypted'],
              prop: 'subject_address_additive',
              type: 'additive',
              initialValue: { ...ENTITY_ADDRESS_INITIAL },
              button: 'Add Another Address',
              group: [
                {
                  label: 'Address',
                  path: ['entities'],
                  prop: 'address_subheader',
                  width: '100%',
                  type: 'subheader',
                },
                {
                  label: '11. Address',
                  path: ['entities', PAGE_INDEX, 'entity_addresses_encrypted', INDEX_REFERENCE, 'address'],
                  prop: 'question_11_address',
                  width: '20.375rem',
                  type: 'textfield',
                  canBeUnknown: 'REQUIRED',
                  unknownSectionController: ['entities', PAGE_INDEX, 'info_unavailable'],
                },
                {
                  label: '12. City',
                  path: ['entities', PAGE_INDEX, 'entity_addresses_encrypted', INDEX_REFERENCE, 'city'],
                  prop: 'question_12_city',
                  width: '20.375rem',
                  type: 'textfield',
                  canBeUnknown: 'REQUIRED',
                  unknownSectionController: ['entities', PAGE_INDEX, 'info_unavailable'],
                },
                {
                  label: '13. State',
                  options: STATES_AND_MILITARY.options,
                  placeholder: 'Select',
                  path: ['entities', PAGE_INDEX, 'entity_addresses_encrypted', INDEX_REFERENCE, 'state'],
                  prop: 'question_13_state',
                  width: '15rem',
                  type: 'select',
                  canBeUnknown: 'REQUIRED',
                  unknownSectionController: ['entities', PAGE_INDEX, 'info_unavailable'],
                },
                {
                  label: '14. Zip/Postal Code',
                  path: ['entities', PAGE_INDEX, 'entity_addresses_encrypted', INDEX_REFERENCE, 'zip'],
                  prop: 'question_14_zip',
                  width: '15rem',
                  type: 'textfield',
                  canBeUnknown: 'REQUIRED',
                  unknownSectionController: ['entities', PAGE_INDEX, 'info_unavailable'],
                  validation: {
                    validator: (
                      createRegExValidationFn('alphanumeric')
                    ),
                  },
                },
                {
                  label: '15. Country',
                  options: COUNTRIES.options,
                  path: ['entities', PAGE_INDEX, 'entity_addresses_encrypted', INDEX_REFERENCE, 'country'],
                  placeholder: 'Select',
                  prop: 'question_15_country',
                  width: '20.375rem',
                  type: 'select',
                  canBeUnknown: 'REQUIRED',
                  unknownSectionController: ['entities', PAGE_INDEX, 'info_unavailable'],
                },
              ],
            },
          ],
        },
        {
          label: '18. Form of identification of subject',
          type: 'section',
          prop: 'sar_entity_ids',
          group: [
            {
              options: [
                { label: 'Form of identification unknown' },
              ],
              path: ['entities', PAGE_INDEX, 'sar_entity_ids_unknown'],
              prop: 'sar_entity_ids_unknown',
              type: 'checkbox-single',
            },
            {
              path: ['entities', PAGE_INDEX, 'sar_entity_ids'],
              prop: 'question_18_additive',
              type: 'additive',
              initialValue: { ...SAR_ENTITY_IDS_INITIAL },
              button: 'Add Another Form of ID',
              group: [
                {
                  label: 'ID',
                  path: ['entities'],
                  prop: 'id_subheader',
                  width: '100%',
                  type: 'subheader',
                },
                {
                  label: 'Type',
                  path: ['entities', PAGE_INDEX, 'sar_entity_ids', INDEX_REFERENCE, 'id_type'],
                  placeholder: 'Select',
                  prop: 'question_18_type',
                  width: '20%',
                  type: 'select',
                  validation: {
                    dependency: ['entities', PAGE_INDEX, 'sar_entity_ids_unknown'],
                    validator: (value, sectionIsUnknown) => (
                      !sectionIsUnknown && !value
                        ? { required: true }
                        : { required: false }
                    ),
                  },
                  options: [
                    {
                      label: 'Driver\'s license/State ID',
                      value: 'state_id',
                    },
                    {
                      label: 'Passport',
                      value: 'passport',
                    },
                    {
                      label: 'Alien registration',
                      value: 'alien_registration',
                    },
                    {
                      label: 'Other',
                      value: 'other',
                    },
                  ],
                },
                {
                  label: 'Other',
                  optional: 'If other, please specify',
                  path: ['entities', PAGE_INDEX, 'sar_entity_ids', INDEX_REFERENCE, 'other_value'],
                  prop: 'question_18_other',
                  width: '15rem',
                  type: 'textfield',
                },
                {
                  label: 'Number',
                  path: ['entities', PAGE_INDEX, 'sar_entity_ids', INDEX_REFERENCE, 'id_num'],
                  prop: 'question_18_number',
                  width: '15rem',
                  type: 'textfield',
                },
                {
                  label: 'Country',
                  path: ['entities', PAGE_INDEX, 'sar_entity_ids', INDEX_REFERENCE, 'country'],
                  placeholder: 'Select',
                  prop: 'question_18_country',
                  width: '15rem',
                  type: 'select',
                  options: COUNTRIES.options,
                },
                {
                  label: 'Issuing State',
                  path: ['entities', PAGE_INDEX, 'sar_entity_ids', INDEX_REFERENCE, 'state'],
                  placeholder: 'Select',
                  prop: 'question_18_state',
                  width: '15rem',
                  type: 'select',
                  options: STATES.options,
                },
              ],
            },
          ],
        },
        {
          label: '24. Relationship of the subject to an institution listed in Part III or IV(check all that apply)',
          prop: '',
          type: 'section',
          group: [
            {
              path: ['entities', PAGE_INDEX, 'relationship_tins'],
              prop: 'question_24_additive',
              type: 'additive',
              initialValue: { ...RELATIONSHIP_TINS_INITIAL },
              button: 'Add Another Institution',
              group: [
                {
                  label: 'Relationship',
                  path: ['entities'],
                  prop: 'relationship_subheader',
                  width: '100%',
                  type: 'subheader',
                },
                {
                  label: 'A. Institution TIN',
                  path: ['entities', PAGE_INDEX, 'relationship_tins', INDEX_REFERENCE, 'institution_tin'],
                  prop: 'question_24_intitution_tin',
                  width: '15rem',
                  type: 'textfield',
                },
                {
                  label: '',
                  path: ['entities', PAGE_INDEX, 'relationship_tins', INDEX_REFERENCE, 'position'],
                  prop: 'question_24_checkbox',
                  width: '100%',
                  type: 'checkbox-multi',
                  options: QUESTION_24_CHECKBOX_OPTIONS,
                },
                {
                  label: 'Other',
                  optional: 'If other, please specify',
                  path: ['entities', PAGE_INDEX, 'relationship_tins', INDEX_REFERENCE, 'other_value'],
                  prop: 'question_24_other',
                  width: '15rem',
                  type: 'textfield',
                },
                {
                  label: '',
                  path: ['entities'],
                  prop: 'question_25',
                  width: '100%',
                  type: 'multi',
                  multiTypes: [
                    {
                      label: '25. Status of relationship',
                      path: ['entities', PAGE_INDEX, 'relationship_tins', INDEX_REFERENCE, 'status'],
                      placeholder: 'Select',
                      prop: 'question_25_relationship',
                      width: '15rem',
                      type: 'select',
                      options: [
                        { label: 'Resigned', value: 'resigned' },
                        { label: 'Relationship Continues', value: 'relationship continues' },
                        { label: 'Suspended/Barred', value: 'suspended/barred' },
                        { label: 'Terminated', value: 'terminated' },
                      ],
                      validation: {
                        dependency: ['entities', PAGE_INDEX, 'relationship_tins', INDEX_REFERENCE, 'position'],
                        validator: (value, position) => (
                          ((position.employee || position.officer || position['owner or controlling shareholder']) && !value)
                            ? { required: true }
                            : { required: false }
                        ),
                      },
                    },
                    {
                      label: '26. Action date',
                      path: ['entities', PAGE_INDEX, 'relationship_tins', INDEX_REFERENCE, 'action_date'],
                      prop: 'question_26_action_date',
                      width: '15rem',
                      type: 'textfield',
                      validation: {
                        dependency: ['entities', PAGE_INDEX, 'relationship_tins', INDEX_REFERENCE, 'status'],
                        validator: (value, statusOfRelationship) => (
                          statusOfRelationship && statusOfRelationship !== 'relationship continues' && !value
                            ? { required: true }
                            : { required: false }
                        ),
                      },
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          label: '27. Financial institution TIN and account number(s) affected that are related to subject',
          prop: '',
          type: 'section',
          group: [
            {
              label: '',
              prop: 'question_27',
              path: ['entities', PAGE_INDEX, 'financial_tins_unknown'],
              width: '',
              type: 'checkbox-single',
              options: [
                {
                  label: 'No known accounts involved',
                },
              ],
            },
            {
              path: ['entities', PAGE_INDEX, 'financial_tins'],
              prop: 'question_27_additive',
              type: 'additive',
              initialValue: { ...FINANCIAL_TINS_INITIAL },
              button: 'Add Another Financial Institution',
              group: [
                {
                  label: 'Financial Institution',
                  path: ['entities'],
                  prop: 'financial_institution_subheader',
                  width: '100%',
                  type: 'subheader',
                },
                {
                  label: '',
                  path: ['entities'],
                  prop: 'question_27_tins',
                  width: '100%',
                  type: 'multi',
                  multiTypes: [
                    {
                      label: 'Institution TIN',
                      path: ['entities', PAGE_INDEX, 'financial_tins', INDEX_REFERENCE, 'institution_tin'],
                      prop: 'question_27_tin',
                      width: '15rem',
                      type: 'textfield',
                      validation: {
                        dependency: ['entities', PAGE_INDEX, 'financial_tins_unknown'],
                        validator: (value, sectionIsUnknown) => (
                          !sectionIsUnknown && !value
                            ? { required: true }
                            : { required: false }
                        ),
                      },
                    },
                    {
                      label: '',
                      path: ['entities', PAGE_INDEX, 'financial_tins', INDEX_REFERENCE, 'non_us_financial_institution'],
                      prop: 'question_27_non_financial_institution',
                      width: '',
                      type: 'checkbox-single',
                      options: [
                        { label: 'Non-US Financial Institution' },
                      ],
                    },
                  ],
                },
                {
                  label: '',
                  path: ['entities', PAGE_INDEX, 'financial_tins', PARENT_INDEX, 'accounts'],
                  prop: 'question_27_account_numbers_additive',
                  type: 'additive',
                  initialValue: {
                    account_number: '',
                    closed: false,
                  },
                  button: 'Add Another Account Number',
                  group: [
                    {
                      label: '',
                      path: ['entities'],
                      prop: 'question_27_account_numbers',
                      width: '100%',
                      type: 'multi',
                      multiTypes: [
                        {
                          label: 'Account Number',
                          path: ['entities', PAGE_INDEX, 'financial_tins', PARENT_INDEX, 'accounts', INDEX_REFERENCE, 'account_number'],
                          prop: 'question_27_account_number',
                          width: '15rem',
                          type: 'textfield',
                          validation: {
                            dependency: ['entities', PAGE_INDEX, 'financial_tins_unknown'],
                            validator: (value, sectionIsUnknown) => (
                              !sectionIsUnknown && !value
                                ? { required: true }
                                : { required: false }
                            ),
                          },
                        },
                        {
                          label: '',
                          path: ['entities', PAGE_INDEX, 'financial_tins', PARENT_INDEX, 'accounts', INDEX_REFERENCE, 'closed'],
                          prop: 'question_27_closed',
                          width: '',
                          type: 'checkbox-single',
                          options: [
                            { label: 'Closed' },
                          ],
                        },
                      ],
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
    [ACTIVITY_INFORMATION]: {
      header: 'Activity Information',
      pageLabel: 'Suspicious Activity Information',
      subheader: ['Select all the transactions relevant to this report to autogenerate the fields below:'],
      controls: [
        {
          type: 'table',
          prop: '',
        },
        {
          question: '29',
          label: 'Amount involved in this report',
          path: ['activity_information', 'amount_involved'],
          prop: 'question_29_amount',
          width: '40%',
          type: 'textfield',
          canBeUnknown: 'REQUIRED',
          validation: {
            dependency: ['activity_information', 'no_amount_involved'],
            validator: (value, dependency) => ({
              amountAboveZero: !dependency
                ? value && nullOrFormatToInteger(value) <= 0 : false,
              required: !dependency && !value,
              currencyFormat: !dependency && Number.isNaN(nullOrFormatToInteger(value)),
            }),
          },
        },
        {
          label: 'No amount involved',
          path: ['activity_information', 'no_amount_involved'],
          prop: 'question_29_no_amount',
          width: '40%',
          type: 'checkbox-single',
          customizations: {
            singleCheckboxInOneRow: true,
          },
          options: [
            { label: '' },
          ],
        },
        {
          question: '30',
          label: 'When did the suspicious activity occur?',
          prop: 'question_30',
          width: '',
          type: 'multi',
          multiTypes: [
            {
              label: 'From',
              path: ['activity_information', 'activity_start_date'],
              prop: 'question_30_from',
              width: '25%',
              type: 'textfield',
              validation: {
                validator: (
                  createRegExValidationFn('dateFormat')
                ),
              },
              required: true,
              placeholder: 'MM/DD/YYYY',
            },
            {
              label: 'To',
              path: ['activity_information', 'activity_end_date'],
              prop: 'question_30_to',
              width: '25%',
              type: 'textfield',
              validation: {
                validator: (
                  createRegExValidationFn('dateFormat')
                ),
              },
              placeholder: 'MM/DD/YYYY',
            },
          ],
        },
        {
          question: '31',
          label: 'Cumulative amount (only applicable when Continuing activity reports)',
          path: ['activity_information', 'cumulative_amount'],
          prop: 'question_31',
          width: '550px',
          tooltip: 'Enter the total of all known dollar activity related to this SAR. If no amount is known or there is no dollar activity involved, enter zero (0). Please explain any unknown values in Part V.',
          type: 'textfield',
          disabled: {
            dependency: ['is_continuing'],
            isFieldDisabled: isContinuing => !isContinuing,
          },
        },
        {
          question: '32',
          label: 'Structuring',
          path: ['activity_information', 'fraud_types', 'structuring'],
          prop: 'question_32',
          width: '',
          type: 'checkbox-multi',
          options: QUESTION_32_CHECKBOX_OPTIONS,
        },
        {
          label: 'Other',
          optional: 'If other, please specify',
          path: ['activity_information', 'fraud_types', 'structuring', 'other_value'],
          prop: 'question_32_other',
          width: '35%',
          type: 'textfield',
        },
        {
          question: '33',
          label: 'Terrorist Financing',
          path: ['activity_information', 'fraud_types', 'terr_fin'],
          prop: 'question_33',
          width: '',
          type: 'checkbox-multi',
          options: QUESTION_33_CHECKBOX_OPTIONS,
        },
        {
          label: 'Other',
          optional: 'If other, please specify',
          path: ['activity_information', 'fraud_types', 'terr_fin', 'other_value'],
          prop: 'question_33_other',
          width: '35%',
          type: 'textfield',
        },
        {
          question: '34',
          label: 'Fraud',
          path: ['activity_information', 'fraud_types', 'fraud'],
          prop: 'question_34',
          width: '',
          type: 'checkbox-multi',
          options: QUESTION_34_CHECKBOX_OPTIONS,
        },
        {
          label: 'Other',
          optional: 'If other, please specify',
          path: ['activity_information', 'fraud_types', 'fraud', 'other_value'],
          prop: 'question_34_other',
          width: '35%',
          type: 'textfield',
        },
        {
          question: '35',
          label: 'Gaming activities',
          path: ['activity_information', 'fraud_types', 'gaming'],
          prop: 'question_35',
          width: '',
          type: 'checkbox-multi',
          options: QUESTION_35_CHECKBOX_OPTIONS,
        },
        {
          label: 'Other',
          optional: 'If other, please specify',
          path: ['activity_information', 'fraud_types', 'gaming', 'other_value'],
          prop: 'question_35_other',
          width: '35%',
          type: 'textfield',
        },
        {
          question: '36',
          label: 'Money Laundering',
          path: ['activity_information', 'fraud_types', 'laundering'],
          prop: 'question_36',
          width: '',
          type: 'checkbox-multi',
          options: QUESTION_36_CHECKBOX_OPTIONS,
        },
        {
          label: 'Other',
          optional: 'If other, please specify',
          path: ['activity_information', 'fraud_types', 'laundering', 'other_value'],
          prop: 'question_36_other',
          width: '35%',
          type: 'textfield',
        },
        {
          question: '37',
          label: 'Identification/Documentation',
          path: ['activity_information', 'fraud_types', 'documentation'],
          prop: 'question_37',
          width: '',
          type: 'checkbox-multi',
          options: QUESTION_37_CHECKBOX_OPTIONS,
        },
        {
          label: 'Other',
          optional: 'If other, please specify',
          path: ['activity_information', 'fraud_types', 'documentation', 'other_value'],
          prop: 'question_37_other',
          width: '35%',
          type: 'textfield',
        },
        {
          question: '38',
          label: 'Other Suspicious Activities',
          path: ['activity_information', 'fraud_types', 'misc'],
          prop: 'question_38',
          width: '',
          type: 'checkbox-multi',
          options: QUESTION_38_CHECKBOX_OPTIONS,
        },
        {
          label: 'Other',
          optional: 'If other, please specify',
          path: ['activity_information', 'fraud_types', 'misc', 'other_value'],
          prop: 'question_38_other',
          width: '35%',
          type: 'textfield',
        },
        {
          question: '39',
          label: 'Insurance',
          path: ['activity_information', 'fraud_types', 'insurance'],
          prop: 'question_39',
          width: '',
          type: 'checkbox-multi',
          options: QUESTION_39_CHECKBOX_OPTIONS,
        },
        {
          label: 'Other',
          optional: 'If other, please specify',
          path: ['activity_information', 'fraud_types', 'insurance', 'other_value'],
          prop: 'question_39_other',
          width: '35%',
          type: 'textfield',
        },
        {
          question: '40',
          label: 'Securities / Futures / Options',
          path: ['activity_information', 'fraud_types', 'securities'],
          prop: 'question_40',
          width: '',
          type: 'checkbox-multi',
          options: QUESTION_40_CHECKBOX_OPTIONS,
        },
        {
          label: 'Other',
          optional: 'If other, please specify',
          path: ['activity_information', 'fraud_types', 'securities', 'other_value'],
          prop: 'question_40_other',
          width: '35%',
          type: 'textfield',
        },
        {
          question: '41',
          label: 'Mortgage Fraud',
          path: ['activity_information', 'fraud_types', 'mortgage'],
          prop: 'question_41',
          width: '',
          type: 'checkbox-multi',
          options: QUESTION_41_CHECKBOX_OPTIONS,
        },
        {
          label: 'Other',
          optional: 'If other, please specify',
          path: ['activity_information', 'fraud_types', 'mortgage', 'other_value'],
          prop: 'question_41_other',
          width: '35%',
          type: 'textfield',
        },
        {
          question: '42',
          label: 'Cyber event',
          path: ['activity_information', 'fraud_types', 'cyber'],
          prop: 'question_42',
          width: '',
          type: 'checkbox-multi',
          options: QUESTION_42_CHECKBOX_OPTIONS,
        },
        {
          label: 'Other',
          optional: 'If other, please specify',
          path: ['activity_information', 'fraud_types', 'cyber', 'other_value'],
          prop: 'question_42_other',
          width: '35%',
          type: 'textfield',
        },
        {
          question: '45',
          label: 'Were any of the following product type(s) involved in the suspicious activity? (Check all that apply)',
          path: ['activity_information', 'fraud_types', 'product'],
          prop: 'question_45',
          width: '',
          type: 'checkbox-multi',
          options: QUESTION_45_CHECKBOX_OPTIONS,
        },
        {
          label: 'Other',
          optional: 'If other, please specify',
          path: ['activity_information', 'fraud_types', 'product', 'other_value'],
          prop: 'question_45_other',
          width: '35%',
          type: 'textfield',
        },
        {
          question: '46',
          label: 'Were any of the following instrument type(s) payment mechanism(s) involved in the suspicious activity? (Check all that apply)',
          path: ['activity_information', 'fraud_types', 'instrument'],
          prop: 'question_46',
          width: '',
          type: 'checkbox-multi',
          options: QUESTION_46_CHECKBOX_OPTIONS,
        },
        {
          label: 'Other',
          optional: 'If other, please specify',
          path: ['activity_information', 'fraud_types', 'instrument', 'other_value'],
          prop: 'question_46_other',
          width: '35%',
          type: 'textfield',
        },
        {
          label: '43. IP Address (enter the IP address/date/timestamp of the subject\'s electronic internet based contact with the financial institution, if known)',
          prop: '',
          type: 'section',
          group: [
            {
              headingSmall: true,
              path: ['activity_information', 'ip_addresses'],
              prop: 'question_43_additive',
              type: 'additive',
              initialValue: { ...IP_ADDRESSES_INITIAL },
              button: 'Add Another IP Address',
              group: [
                {
                  label: 'IP Address',
                  path: ['activity_information'],
                  prop: 'ip_address_subheader',
                  width: '100%',
                  type: 'subheader',
                },
                {
                  path: ['activity_information', 'ip_addresses', INDEX_REFERENCE, 'address'],
                  prop: 'question_43_ip_address',
                  width: '15rem',
                  type: 'textfield',
                },
                {
                  label: 'Date',
                  path: ['activity_information', 'ip_addresses', INDEX_REFERENCE, 'date'],
                  prop: 'question_43_date',
                  width: '15rem',
                  type: 'textfield',
                },
                {
                  label: 'Timestamp (UTC)',
                  path: ['activity_information', 'ip_addresses', INDEX_REFERENCE, 'timestamp'],
                  prop: 'question_43_timestamp',
                  width: '15rem',
                  type: 'textfield',
                },
              ],
            },
          ],
        },
        {
          label: '44. Cyber Event Indicators (select the appropriate indicator(s) from the drop-down list and provide the associated supporting information)',
          prop: '',
          type: 'section',
          group: [
            {
              path: ['activity_information', 'cyber_frauds'],
              prop: 'question_44_additive',
              headingSmall: true,
              type: 'additive',
              initialValue: { ...CYBER_FRAUDS_INITIAL },
              button: 'Add Another Cyber Event Indicator',
              group: [
                {
                  label: 'Cyber Event',
                  path: ['activity_information'],
                  prop: 'cyber_event_subheader',
                  width: '100%',
                  type: 'subheader',
                },
                {
                  label: '',
                  path: ['activity_information'],
                  prop: 'question_44',
                  width: '%',
                  type: 'multi',
                  multiTypes: [
                    {
                      label: 'Event type',
                      prop: 'question_44_event_type',
                      type: 'select',
                      path: ['activity_information', 'cyber_frauds', INDEX_REFERENCE, 'sar_cyber_event_type'],
                      placeholder: 'Select',
                      width: '20.375rem',
                      options: [
                        {
                          label: 'Command and control IP address',
                          value: 'command_control_ip_address',
                        },
                        {
                          label: 'Command and control URL/domain',
                          value: 'command_control_URL',
                        },
                        {
                          label: 'Malware MD5, Malware SHA-1, or Malware SHA-256',
                          value: 'malware',
                        },
                        {
                          label: 'Media Access Control (MAC) Address',
                          value: 'MAC',
                        },
                        {
                          label: 'Port',
                          value: 'port',
                        },
                        {
                          label: 'Suspicious e-mail address',
                          value: 'suspicious_email_address',
                        },
                        {
                          label: 'Suspicious file name',
                          value: 'suspicious_file_name',
                        },
                        {
                          label: 'Suspicious IP address',
                          value: 'suspicious_ip_address',
                        },
                        {
                          label: 'Suspicious URL/domain',
                          value: 'suspicious_url_domain',
                        },
                        {
                          label: 'Targeted system',
                          value: 'targeted_system',
                        },
                        {
                          label: 'Other',
                          value: 'other',
                        },
                      ],
                    },
                    {
                      label: 'Other',
                      optional: 'If other, please specify',
                      path: ['activity_information', 'cyber_frauds', INDEX_REFERENCE, 'other_value'],
                      prop: 'question_44_event_type_other',
                      width: '15rem',
                      type: 'textfield',
                    },
                  ],
                },
                {
                  label: 'Event value',
                  path: ['activity_information', 'cyber_frauds', INDEX_REFERENCE, 'sar_cyber_event_value'],
                  prop: 'question_44_event_value',
                  width: '15rem',
                  type: 'textfield',
                },
                {
                  label: 'Date',
                  path: ['activity_information', 'cyber_frauds', INDEX_REFERENCE, 'sar_cyber_event_date'],
                  prop: 'question_44_event_date',
                  width: '15rem',
                  type: 'textfield',
                },
              ],
            },
          ],
        },
        {
          question: 47,
          label: 'Commodity type (if applicable)',
          path: ['activity_information', 'commodity_type'],
          prop: 'question_47_additive',
          type: 'additive',
          initialValue: '',
          button: 'Add Another Commodity Type',
          group: [{
            path: ['activity_information', 'commodity_type', INDEX_REFERENCE],
            type: 'textfield',
            prop: 'question_47',
          }],
        },
        {
          question: 48,
          label: 'Product/Instrument description (if needed)',
          path: ['activity_information', 'product_description'],
          prop: 'question_48_additive',
          type: 'additive',
          initialValue: '',
          button: 'Add Another Description',
          group: [{
            path: ['activity_information', 'product_description', INDEX_REFERENCE],
            type: 'textfield',
            prop: 'question_48',
          }],
        },
        {
          question: 49,
          label: 'Market where traded',
          path: ['activity_information', 'market_traded'],
          prop: 'question_49_additive',
          type: 'additive',
          initialValue: '',
          button: 'Add Another Market',
          group: [{
            path: ['activity_information', 'market_traded', INDEX_REFERENCE],
            type: 'textfield',
            prop: 'question_49',
          }],
        },
        {
          question: 50,
          label: 'CUSIP number',
          path: ['activity_information', 'cusip_number'],
          prop: 'question_50_additive',
          type: 'additive',
          initialValue: '',
          button: 'Add Another CUSIP Number',
          group: [{
            path: ['activity_information', 'cusip_number', INDEX_REFERENCE],
            type: 'textfield',
            prop: 'question_50',
          }],
        },
      ],
    },
    [NARRATIVE]: {
      header: 'Narrative',
      pageLabel: 'Narrative',
      controls: [
        {
          label: 'If report type is continuing activity report, please explain any unknown values for cumulative amount.',
          path: ['activity_information', 'narrative'],
          prop: 'narrative',
          required: true,
          width: '700px',
          type: 'textarea',
        },
        {
          label: 'Filing Institution Note to FinCEN',
          optional: 'Optional',
          path: ['activity_information', 'filing_note'],
          prop: 'question_2',
          type: 'textfield',
          validation: {
            validator: value => ({ noteToFincen: value?.length > 50 }),
          },
        },
        {
          prop: 'ninety_day_reminder_inputs',
          type: 'multi',
          style: { alignItems: 'center', paddingTop: 'var(--cl-space-4)' },
          multiTypes: [
            {
              type: 'checkbox-single',
              path: ['form_is_ninety_day_review'],
              prop: 'form_is_ninety_day_review',
              options: [
                {
                  label: 'Notify to review this report for continuing suspicious activity',
                },
              ],
              style: { margin: 0 },
            },
            {
              options: [...NOTIFICATION_DAYS_OPTIONS],
              path: ['notification_days'],
              prop: 'notification_days',
              type: 'select',
              width: '200px',
            },
            {
              type: 'text',
              prop: 'notification_days_helper_text',
              label: 'days before the 90 day review',
            },
          ],
        },
      ],
    },
    [NEXT_STEPS]: {
      header: 'Next Steps',
      subheader: ['You\'ve completed all steps of this report!'],
      controls: [
        {
          label: '',
          prop: '',
          width: '',
          type: '',
        },
      ],
    },
  },
};

export const FORM_CONTROLS_V2: Form = {
  steps: {
    [SAR_FILING_INSTITUTION]: {
      header: 'Part 1: Filing Institution Contact Information',
      pageLabel: 'Filing Institution Contact Information',
      subheader: ['Please complete the following fields.'],
      controls: [
        {
          label: '',
          prop: 'question_79',
          type: 'multi',
          multiTypes: [
            {
              label: '79. Type of financial institution',
              path: ['filing_institution', 'type_of_financial_institution', '0'],
              prop: 'question_79_type',
              width: '15rem',
              type: 'select',
              placeholder: 'Select',
              required: true,
              options: [
                {
                  label: 'Depository institution',
                  value: 'depository_institution',
                },
                {
                  label: 'Casino/Card Club',
                  value: 'casino_card_club',
                },
                {
                  label: 'Insurance company',
                  value: 'insurance_company',
                },
                {
                  label: 'MSB',
                  value: 'MSB',
                },
                {
                  label: 'Securities/Futures',
                  value: 'securities_futures',
                },
                {
                  label: 'Loan or Finance Company',
                  value: 'loan_or_finance_company',
                },
                {
                  label: 'Housing GSE',
                  value: 'housing_gse',
                },
                {
                  label: 'Other',
                  value: 'other',
                },
              ],
            },
            {
              label: 'Other',
              optional: 'If other, please specify',
              path: ['filing_institution', 'type_of_financial_institution', '1'],
              prop: 'question_79_other',
              width: '15rem',
              type: 'textfield',
            },
          ],
        },
        {
          label: '75. Primary federal regulator',
          path: ['filing_institution', 'primary_federal_regulator'],
          prop: 'question_75',
          width: '15rem',
          type: 'select',
          placeholder: 'Select',
          required: true,
          options: PRIMARY_REGULATOR_TYPE_OPTIONS,
        },
        {
          label: '76. Filer name (Holding company, lead financial institution, or agency, if applicable)',
          path: ['filing_institution', 'filer_name'],
          prop: 'question_76',
          required: true,
          width: '31rem',
          type: 'textfield',
        },
        {
          label: '',
          prop: 'question_77',
          type: 'multi',
          multiTypes: [
            {
              label: '77. TIN',
              path: ['filing_institution', 'tin'],
              prop: 'question_77_tin',
              width: '20.375rem',
              type: 'textfield',
              required: true,
              validation: {
                dependency: ['filing_institution', 'tin_type'],
                validator: (value, dependencyValue) => {
                  if (!value) {
                    return { tin: false, foreignTin: false };
                  }
                  if (dependencyValue === 'EIN' || dependencyValue === 'ssn-itin') {
                    return value.match(/^([0-9][0-9][0-9][0-9][0-9][0-9][0-9][0-9][0-9])$/)
                      ? { tin: false, foreignTin: false }
                      : { tin: true, foreignTin: false };
                  }
                  return value.length <= 25
                    ? { tin: false, foreignTin: false }
                    : { tin: false, foreignTin: true };
                },
              },
            },
            {
              label: '78. TIN type',
              path: ['filing_institution', 'tin_type'],
              prop: 'question_78',
              width: '15rem',
              type: 'select',
              placeholder: 'Select',
              required: true,
              options: [
                {
                  label: 'EIN',
                  value: 'EIN',
                },
                {
                  label: 'SSN-ITIN',
                  value: 'ssn-itin',
                },
                {
                  label: 'Foreign',
                  value: 'foreign',
                },
              ],
            },
          ],
        },
        {
          label: '80. Type of Securities and Futures institution or individual filing this report - check box(es) for functions that apply to this report',
          path: ['filing_institution', 'type_of_securities_institution', 'checkboxes'],
          prop: 'question_80',
          width: '100%',
          type: 'checkbox-multi',
          options: QUESTION_80_CHECKBOX_OPTIONS,
        },
        {
          label: 'Other',
          path: ['filing_institution', 'type_of_securities_institution', 'other'],
          optional: 'If other, please specify',
          prop: 'question_80_other',
          width: '15rem',
          type: 'textfield',
        },
        {
          question: 81,
          label: 'Financial institution identification',
          optional: 'Optional',
          prop: 'question_81',
          required: false,
          type: 'multi',
          multiTypes: [
            {
              label: 'Type',
              path: ['filing_institution', 'institution_id_type'],
              prop: 'question_81_type',
              width: '20.375rem',
              type: 'select',
              placeholder: 'Select',
              required: false,
              options: [
                {
                  label: 'Central Registration Depository (CRD) number',
                  value: 'crd_number',
                },
                {
                  label: 'Investment Adviser Registeration Depository (IARD) number',
                  value: 'iard_number',
                },
                {
                  label: 'National Futures Association (NFA) Number',
                  value: 'nfa_number',
                },
                {
                  label: 'Research, Statistics, Supervision, and Discount (RSSD) number',
                  value: 'rssd_number',
                },
                {
                  label: 'Securities and Exchange Commission (SEC) number',
                  value: 'sec_number',
                },
                {
                  label: 'National Association of Insurance Commissioners (NAIC Code)',
                  value: 'naic_code',
                },
                {
                  label: 'Mortgage (NMLS ID)',
                  value: 'mortgage_nmls_id',
                },
              ],
            },
            {
              label: 'Number',
              path: ['filing_institution', 'institution_id'],
              prop: 'question_81_number',
              width: '15rem',
              type: 'textfield',
            },
          ],
        },
        {
          label: '82. Address',
          path: ['filing_institution', 'address', 'address'],
          prop: 'question_82_address',
          required: true,
          width: '31rem',
          type: 'textfield',
        },
        {
          label: '83. City',
          path: ['filing_institution', 'address', 'city'],
          prop: 'question_83_city',
          required: true,
          width: '31rem',
          type: 'textfield',
        },
        {
          label: '',
          prop: 'question_84',
          width: '100%',
          type: 'multi',
          multiTypes: [
            {
              label: '84. State',
              path: ['filing_institution', 'address', 'state'],
              prop: 'question_84_state',
              width: '15rem',
              type: 'select',
              placeholder: 'Select',
              options: STATES.options,
              validation: {
                dependency: ['filing_institution', 'address', 'country'],
                validator: (state, country) => (
                  country === US.value && !state
                    ? { required: true }
                    : { required: false }
                ),
              },
            },
            {
              label: '85. ZIP/Postal Code',
              path: ['filing_institution', 'address', 'zip'],
              prop: 'question_85_zip',
              required: true,
              width: '15rem',
              type: 'textfield',
              validation: {
                validator: (
                  createRegExValidationFn('alphanumeric')
                ),
              },
            },
            {
              label: '86. Country',
              path: ['filing_institution', 'address', 'country'],
              prop: 'question_86_country',
              required: true,
              width: '15rem',
              type: 'select',
              placeholder: 'Select',
              options: COUNTRIES.options,
            },
          ],
        },
        {
          label: '87. Alternate name, e.g. AKA: individual or trade name, DBA - business entity',
          optional: 'Optional',
          path: ['filing_institution', 'alternate_name'],
          prop: 'question_87',
          width: '31rem',
          type: 'textfield',
        },
        {
          label: '88. Internal control/file number',
          optional: 'Optional',
          path: ['filing_institution', 'internal_control_number'],
          prop: 'question_88',
          width: '31rem',
          type: 'textfield',
        },
        {
          label: '89. LE contact agency',
          optional: 'Optional',
          path: ['filing_institution', 'contact_agency'],
          prop: 'question_89',
          width: '31rem',
          type: 'textfield',
        },
        {
          label: '90. LE contact name',
          optional: 'Optional',
          path: ['filing_institution', 'contact_name'],
          prop: 'question_90',
          width: '31rem',
          type: 'textfield',
        },
        {
          label: '',
          prop: 'question_91',
          width: '31rem',
          type: 'multi',
          multiTypes: [
            {
              label: '91. LE contact phone number (include area code)',
              path: ['filing_institution', 'phone_number', '0'],
              prop: 'question_91',
              width: '20.375rem',
              type: 'textfield',
            },
            {
              label: 'Ext',
              optional: 'Optional',
              path: ['filing_institution', 'phone_number', '1'],
              prop: 'question_91_ext',
              width: '15rem',
              type: 'textfield',
            },
          ],
        },
        {
          label: '92. LE contact date',
          optional: 'Optional',
          path: ['filing_institution', 'contact_date'],
          prop: 'question_92',
          width: '20.375rem',
          type: 'textfield',
        },
        {
          label: '93. Filing institution contact office',
          path: ['filing_institution', 'filing_institution_contact_office'],
          prop: 'question_93',
          required: true,
          width: '31rem',
          type: 'textfield',
        },
        {
          label: '',
          path: ['filing_institution'],
          prop: 'question_94_multi',
          width: '',
          type: 'multi',
          multiTypes: [
            {
              label: '94. Filing institution contact phone number (include area code)',
              path: ['filing_institution', 'filing_institution_contact_number', '0'],
              prop: 'question_94',
              required: true,
              width: '31rem',
              type: 'textfield',
            },
            {
              label: 'Ext',
              optional: 'Optional',
              path: ['filing_institution', 'filing_institution_contact_number', '1'],
              prop: 'question_94_ext',
              width: '15rem',
              type: 'textfield',
            },
          ],
        },
      ],
    },
    [SAR_ACTIVITY_INSTITUTION]: {
      header: 'Part 2a: Financial Institution Where Activity Occurred',
      pageLabel: 'Financial Institution Where Activity Occurred',
      subheader: ['Please confirm the following is accurate.'],
      controls: [
        {
          label: '',
          prop: 'question_51',
          type: 'multi',
          multiTypes: [
            {
              label: '51. Type of financial institution',
              path: ['sar_activity_institution', 'type_of_financial_institution', '0'],
              prop: 'question_51_type',
              width: '15rem',
              type: 'select',
              placeholder: 'Select',
              required: true,
              options: [
                {
                  label: 'Depository institution',
                  value: 'depository_institution',
                },
                {
                  label: 'Casino/Card Club',
                  value: 'casino_card_club',
                },
                {
                  label: 'Insurance company',
                  value: 'insurance_company',
                },
                {
                  label: 'MSB',
                  value: 'MSB',
                },
                {
                  label: 'Securities/Futures',
                  value: 'securities_futures',
                },
                {
                  label: 'Loan or Finance Company',
                  value: 'loan_or_finance_company',
                },
                {
                  label: 'Housing GSE',
                  value: 'housing_gse',
                },
                {
                  label: 'Other',
                  value: 'other',
                },
              ],
            },
            {
              label: 'Other',
              optional: 'If other, please specify',
              path: ['sar_activity_institution', 'type_of_financial_institution', '1'],
              prop: 'question_51_other',
              width: '15rem',
              type: 'textfield',
            },
          ],
        },
        {
          label: '52. Primary federal regulator',
          path: ['sar_activity_institution', 'primary_federal_regulator'],
          prop: 'question_52',
          required: true,
          width: '15rem',
          type: 'select',
          placeholder: 'Select',
          options: PRIMARY_REGULATOR_TYPE_OPTIONS,
        },
        {
          label: '53. Type of gaming institution',
          path: ['sar_activity_institution', 'type_of_gaming_institution', 'checkboxes'],
          prop: 'question_53',
          width: '100%',
          type: 'checkbox-multi',
          options: QUESTION_53_CHECKBOX_OPTIONS,
        },
        {
          label: 'Other',
          optional: 'If other, please specify',
          path: ['sar_activity_institution', 'type_of_gaming_institution', 'other'],
          prop: 'question_53_other',
          width: '15rem',
          type: 'textfield',
        },
        {
          label: '54. Type of Securities and Futures institution or individual where activity occurred - check box(es) for functions that apply to this report',
          path: ['sar_activity_institution', 'type_of_securities_institution', 'checkboxes'],
          prop: 'question_54',
          width: '100%',
          type: 'checkbox-multi',
          options: QUESTION_54_CHECKBOX_OPTIONS,
        },
        {
          label: 'Other',
          optional: 'If other, please specify',
          path: ['sar_activity_institution', 'type_of_securities_institution', 'other'],
          prop: 'question_54_other',
          width: '15rem',
          type: 'textfield',
        },
        {
          question: 55,
          label: 'Financial institution identification',
          optional: 'Optional',
          prop: 'question_55',
          required: false,
          type: 'multi',
          multiTypes: [
            {
              label: 'Type',
              path: ['sar_activity_institution', 'institution_id_type'],
              prop: 'question_55_type',
              width: '20.375rem',
              type: 'select',
              placeholder: 'Select',
              required: false,
              options: [
                {
                  label: 'Central Registration Depository (CRD) number',
                  value: 'crd_number',
                },
                {
                  label: 'Investment Adviser Registeration Depository (IARD) number',
                  value: 'iard_number',
                },
                {
                  label: 'National Futures Association (NFA) Number',
                  value: 'nfa_number',
                },
                {
                  label: 'Research, Statistics, Supervision, and Discount (RSSD) number',
                  value: 'rssd_number',
                },
                {
                  label: 'Securities and Exchange Commission (SEC) number',
                  value: 'sec_number',
                },
                {
                  label: 'National Association of Insurance Commissioners (NAIC Code)',
                  value: 'naic_code',
                },
                {
                  label: 'Mortgage (NMLS ID)',
                  value: 'mortgage_nmls_id',
                },
              ],
            },
            {
              label: 'Number',
              path: ['sar_activity_institution', 'institution_id'],
              prop: 'question_55_number',
              width: '15rem',
              type: 'textfield',
            },
          ],
        },
        {
          label: '56. Financial institution\'s role in transaction',
          path: ['sar_activity_institution', 'financial_institution_role'],
          prop: 'question_56',
          width: '100%',
          type: 'checkbox-multi',
          options: QUESTION_56_CHECKBOX_OPTIONS,
        },
        {
          label: '57. Legal name of financial institution',
          path: ['sar_activity_institution', 'legal_name'],
          prop: 'question_57',
          width: '15rem',
          type: 'textfield',
          canBeUnknown: 'REQUIRED',
        },
        {
          label: '58. Alternate name, e.g., AKA - individual or trade name, DBA - business entity',
          path: ['sar_activity_institution', 'alternate_name'],
          prop: 'question_58',
          width: '20.375rem',
          type: 'textfield',
          optional: 'Optional',
        },
        {
          label: '',
          prop: 'question_59',
          required: false,
          type: 'multi',
          multiTypes: [
            {
              label: '59. TIN',
              path: ['sar_activity_institution', 'tin'],
              prop: 'question_59_tin',
              width: '20rem',
              type: 'textfield',
              canBeUnknown: 'REQUIRED',
              validation: {
                dependency: ['sar_activity_institution', 'tin_type'],
                validator: (value, dependencyValue) => {
                  if (!value) {
                    return { tin: false, foreignTin: false };
                  }
                  if (dependencyValue === 'EIN' || dependencyValue === 'ssn-itin') {
                    return value.match(/^([0-9][0-9][0-9][0-9][0-9][0-9][0-9][0-9][0-9])$/)
                      ? { tin: false, foreignTin: false }
                      : { tin: true, foreignTin: false };
                  }
                  return value.length <= 25
                    ? { tin: false, foreignTin: false }
                    : { tin: false, foreignTin: true };
                },
              },
            },
            {
              label: '60. TIN type',
              path: ['sar_activity_institution', 'tin_type'],
              prop: 'question_60',
              width: '15rem',
              type: 'select',
              placeholder: 'Select',
              required: false,
              options: [
                {
                  label: 'EIN',
                  value: 'EIN',
                },
                {
                  label: 'SSN-ITIN',
                  value: 'ssn-itin',
                },
                {
                  label: 'Foreign',
                  value: 'foreign',
                },
              ],
            },
          ],
        },
        {
          label: '61. Address',
          path: ['sar_activity_institution', 'address', 'address'],
          prop: 'question_61_address',
          width: '20.375rem',
          type: 'textfield',
          canBeUnknown: 'REQUIRED',
        },
        {
          label: '62. City',
          path: ['sar_activity_institution', 'address', 'city'],
          prop: 'question_62_city',
          width: '20.375rem',
          type: 'textfield',
          canBeUnknown: 'REQUIRED',
        },
        {
          label: '63. State',
          path: ['sar_activity_institution', 'address', 'state'],
          prop: 'question_63_state',
          width: '15rem',
          type: 'select',
          placeholder: 'Select',
          options: STATES_AND_MILITARY.options,
        },
        {
          label: '64. Zip/Postal Code',
          path: ['sar_activity_institution', 'address', 'zip'],
          prop: 'question_64_zip',
          width: '20.375rem',
          type: 'textfield',
          canBeUnknown: 'REQUIRED',
          validation: {
            validator: (
              createRegExValidationFn('alphanumeric')
            ),
          },
        },
        {
          label: '65. Country',
          options: COUNTRIES.options,
          path: ['sar_activity_institution', 'address', 'country'],
          placeholder: 'Select',
          prop: 'question_65_country',
          width: '20.375rem',
          type: 'select',
          canBeUnknown: 'REQUIRED',
        },
        {
          label: '66. Internal control/file number',
          optional: 'Optional',
          path: ['sar_activity_institution', 'internal_control_number'],
          prop: 'question_66',
          width: '20.375rem',
          type: 'textfield',
        },
        {
          label: '67. Loss to financial institution',
          optional: 'Optional',
          path: ['sar_activity_institution', 'loss_financial_institution'],
          prop: 'question_67',
          width: '20.375rem',
          type: 'textfield',
        },
        {
          label: 'Part 2b: Branch where activity occurred information',
          prop: '',
          type: 'section',
          group: [
            {
              path: ['sar_activity_institution', 'branch_activity'],
              prop: 'branch_activity',
              width: '100%',
              type: 'checkbox-single',
              options: [
                {
                  label: 'If no branch activity involved, check this box',
                },
              ],
            },
            {
              path: ['sar_activity_institution', 'branch_information'],
              prop: 'branch_information_additive',
              type: 'additive',
              initialValue: { ...BRANCH_INFORMATION_INITIAL },
              button: 'Add Another Branch',
              group: [
                {
                  label: 'Branch Information',
                  path: ['sar_activity_institution'],
                  prop: 'branch_subheader',
                  width: '100%',
                  type: 'subheader',
                },
                {
                  label: '68. Branch\'s role in transaction',
                  optional: 'Optional',
                  path: ['sar_activity_institution', 'branch_information', INDEX_REFERENCE, 'branch_role'],
                  prop: 'question_68_role',
                  width: '70%',
                  type: 'checkbox-multi',
                  placeholder: 'Select',
                  options: QUESTION_68_CHECKBOX_OPTIONS,
                },
                {
                  label: '69. Address of branch or office where activity occurred',
                  optional: 'Optional',
                  path: ['sar_activity_institution', 'branch_information', INDEX_REFERENCE, 'address'],
                  prop: 'question_69_address',
                  width: '31rem',
                  type: 'textfield',
                },
                {
                  label: '',
                  path: ['sar_activity_institution'],
                  prop: 'question_71',
                  type: 'multi',
                  width: '100%',
                  multiTypes: [
                    {
                      label: '71. City',
                      optional: 'Optional',
                      path: ['sar_activity_institution', 'branch_information', INDEX_REFERENCE, 'city'],
                      prop: 'question_71_city',
                      width: '15rem',
                      type: 'textfield',
                    },
                    {
                      label: '70. RSSD Number',
                      path: ['sar_activity_institution', 'branch_information', INDEX_REFERENCE, 'rssd_number'],
                      prop: 'question_70_rssd',
                      optional: 'Optional',
                      type: 'textfield',
                      width: '15rem',
                    },
                  ],
                },
                {
                  label: '',
                  path: ['sar_activity_institution'],
                  prop: 'question_72',
                  type: 'multi',
                  width: '100%',
                  multiTypes: [
                    {
                      label: '72. State',
                      optional: 'Optional',
                      path: ['sar_activity_institution', 'branch_information', INDEX_REFERENCE, 'state'],
                      prop: 'question_72_state',
                      width: '13rem',
                      placeholder: 'Select',
                      type: 'select',
                      options: STATES_AND_MILITARY.options,
                    },
                    {
                      label: '73. ZIP/Postal code',
                      path: ['sar_activity_institution', 'branch_information', INDEX_REFERENCE, 'zip'],
                      prop: 'question_73_zip',
                      optional: 'Optional',
                      type: 'textfield',
                      width: '13rem',
                      validation: {
                        validator: (
                          createRegExValidationFn('alphanumeric')
                        ),
                      },
                    },
                    {
                      label: '74. Country',
                      path: ['sar_activity_institution', 'branch_information', INDEX_REFERENCE, 'country'],
                      prop: 'question_74_country',
                      placeholder: 'Select',
                      width: '13rem',
                      type: 'select',
                      options: COUNTRIES.options,
                      validation: {
                        dependency: ['sar_activity_institution', 'branch_activity'],
                        validator: (country, noBranchActivity) => (
                          !country && !noBranchActivity
                            ? { required: true }
                            : { required: false }
                        ),
                      },
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
    ...FORM_CONTROLS_V1.steps,
  },
};

export const FORM_CONTROLS_V3: Form = {
  steps: {
    [OVERVIEW]: {
      pageLabel: 'Report Overview',
      controls: [
        {
          type: 'report-overview',
          prop: '',
        },
      ],
    },
    ...FORM_CONTROLS_V2.steps,
  },
};

const mapValues = controls => (
  controls.reduce((innerAcc, control) => {
    if (
      control.type === 'dropdown'
      || control.type === 'select'
      || (
        ['checkbox', 'checkbox-multi'].includes(control.type)
        && control.options.length !== 1
      )
    ) {
      const map = {};
      control.options.forEach(({ label, name, value }) => {
        map[value] = label || name;
      });
      // state, military and country codes are not mutually exclusive, so we'll nest them within the accumulator
      if (control.options === STATES_AND_MILITARY.options) {
        return { ...innerAcc, state_and_military_codes: map };
      }
      if (control.options === COUNTRIES.options) {
        return { ...innerAcc, country_codes: map };
      }
      if (control.options === STATES.options) {
        return { ...innerAcc, state_codes: map };
      }
      return { ...innerAcc, ...map };
    }
    if (control.type === 'multi') {
      return { ...innerAcc, ...mapValues(control.multiTypes) };
    }
    return innerAcc;
  }, {})
);

export const FORM_LABEL_MAPPINGS = Object.values(FORM_CONTROLS_V3.steps).reduce(
  (acc, { controls }) => {
    const mappedValues = mapValues(controls);
    return { ...acc, ...mappedValues };
  }, {},
);

// this dictionary give us paths to access additive inputs' parent data,
// to be used when transforming an incoming metadata response.
// this only really exists to solve a temporary issue and may be able to be removed after the form is fully refactored
export const ADDITIVE_PARENT_MAPPINGS = {
  question_9_additive: ['entities', PAGE_INDEX, 'alternate_names_encrypted'],
  question_9: ['entities', PAGE_INDEX, 'alternate_names_encrypted'],
  address_subheader: ['entities', PAGE_INDEX, 'entity_addresses_encrypted'],
  subject_address_additive: ['entities', PAGE_INDEX, 'entity_addresses_encrypted'],
  question_11_address: ['entities', PAGE_INDEX, 'entity_addresses_encrypted'],
  question_12_city: ['entities', PAGE_INDEX, 'entity_addresses_encrypted'],
  question_13_state: ['entities', PAGE_INDEX, 'entity_addresses_encrypted'],
  question_14_zip: ['entities', PAGE_INDEX, 'entity_addresses_encrypted'],
  question_15_country: ['entities', PAGE_INDEX, 'entity_addresses_encrypted'],
  id_subheader: ['entities', PAGE_INDEX, 'sar_entity_ids'],
  question_18_additive: ['entities', PAGE_INDEX, 'sar_entity_ids'],
  question_18_type: ['entities', PAGE_INDEX, 'sar_entity_ids'],
  question_18_other: ['entities', PAGE_INDEX, 'sar_entity_ids'],
  question_18_number: ['entities', PAGE_INDEX, 'sar_entity_ids'],
  question_18_country: ['entities', PAGE_INDEX, 'sar_entity_ids'],
  question_18_state: ['entities', PAGE_INDEX, 'sar_entity_ids'],
  question_21_additive: ['entities', PAGE_INDEX, 'phone_numbers_encrypted'],
  question_21_type: ['entities', PAGE_INDEX, 'phone_numbers_encrypted'],
  question_21_ext: ['entities', PAGE_INDEX, 'phone_numbers_encrypted'],
  question_21_value: ['entities', PAGE_INDEX, 'phone_numbers_encrypted'],
  question_22_additive: ['entities', PAGE_INDEX, 'email_addresses_encrypted'],
  question_22: ['entities', PAGE_INDEX, 'email_addresses_encrypted'],
  question_22a_additive: ['entities', PAGE_INDEX, 'websites'],
  question_22a: ['entities', PAGE_INDEX, 'websites'],
  relationship_subheader: ['entities', PAGE_INDEX, 'relationship_tins'],
  question_24_additive: ['entities', PAGE_INDEX, 'relationship_tins'],
  question_24_checkbox: ['entities', PAGE_INDEX, 'relationship_tins'],
  question_24_intitution_tin: ['entities', PAGE_INDEX, 'relationship_tins'],
  question_24_other: ['entities', PAGE_INDEX, 'relationship_tins'],
  question_25: ['entities', PAGE_INDEX, 'relationship_tins'],
  question_25_relationship: ['entities', PAGE_INDEX, 'relationship_tins'],
  question_26_action_date: ['entities', PAGE_INDEX, 'relationship_tins'],
  financial_institution_subheader: ['entities', PAGE_INDEX, 'financial_tins'],
  question_27_additive: ['entities', PAGE_INDEX, 'financial_tins'],
  question_27_tin: ['entities', PAGE_INDEX, 'financial_tins'],
  question_27_tins: ['entities', PAGE_INDEX, 'financial_tins'],
  question_27_account_numbers_additive: ['entities', PAGE_INDEX, 'financial_tins', PARENT_INDEX, 'accounts'],
  question_27_account_numbers: ['entities', PAGE_INDEX, 'financial_tins', PARENT_INDEX, 'accounts'],
  question_27_account_number: ['entities', PAGE_INDEX, 'financial_tins', PARENT_INDEX, 'accounts'],
  ip_address_subheader: ['activity_information', 'ip_addresses'],
  question_43_additive: ['activity_information', 'ip_addresses'],
  question_43_ip_address: ['activity_information', 'ip_addresses'],
  question_43_date: ['activity_information', 'ip_addresses'],
  question_43_timestamp: ['activity_information', 'ip_addresses'],
  cyber_event_subheader: ['activity_information', 'cyber_frauds'],
  question_44_additive: ['activity_information', 'cyber_frauds'],
  question_44: ['activity_information', 'cyber_frauds'],
  question_44_event_type: ['activity_information', 'cyber_frauds'],
  question_44_event_value: ['activity_information', 'cyber_frauds'],
  question_44_event_date: ['activity_information', 'cyber_frauds'],
  question_44_event_type_other: ['activity_information', 'cyber_frauds'],
  question_47_additive: ['activity_information', 'commodity_type'],
  question_47: ['activity_information', 'commodity_type'],
  question_48_additive: ['activity_information', 'product_description'],
  question_48: ['activity_information', 'product_description'],
  question_49_additive: ['activity_information', 'market_traded'],
  question_49: ['activity_information', 'market_traded'],
  question_50_additive: ['activity_information', 'cusip_number'],
  question_50: ['activity_information', 'cusip_number'],
  branch_information_additive: ['sar_activity_institution', 'branch_information'],
  branch_subheader: ['sar_activity_institution', 'branch_information'],
  question_68_role: ['sar_activity_institution', 'branch_information'],
  question_69_address: ['sar_activity_institution', 'branch_information'],
  question_71: ['sar_activity_institution', 'branch_information'],
  question_71_city: ['sar_activity_institution', 'branch_information'],
  question_70_rssd: ['sar_activity_institution', 'branch_information'],
  question_72: ['sar_activity_institution', 'branch_information'],
  question_72_state: ['sar_activity_institution', 'branch_information'],
  question_73_zip: ['sar_activity_institution', 'branch_information'],
  question_74_country: ['sar_activity_institution', 'branch_information'],
};

// I don't like where this is going, but we need a reference
// for nested additive sections to look at their parents' parents~~
// this should only apply to a few things here, but if it ever gets expanded
// then we really should consider revisiting nested additive logic
export const NESTED_ADDITIVE_PARENT_MAPPINGS = {
  question_27_account_numbers_additive: ['entities', PAGE_INDEX, 'financial_tins'],
  question_27_account_numbers: ['entities', PAGE_INDEX, 'financial_tins'],
  question_27_account_number: ['entities', PAGE_INDEX, 'financial_tins'],
};

export const reminderDropdownOptions = new Array(91)
  .fill(null)
  .map((value, index) => ({ label: `${index}`, value: `${index}` }));
